import { Button } from "@/components/ui/button";
import { Toolbar, ToolbarItem } from "@/components/ui/toolbar";
import { ArrowUpOnSquareIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { ReactNode } from "react";
import { InfoModalTrigger } from "@/components/ui/modal";
import { CustomDropdown, CustomDropdownItem } from "@/components/ui/dropdown";
import { PencilSquareIcon } from "@heroicons/react/20/solid";
import { Select } from "@/components/ui/select/select";
import { SelectItem } from "@/components/ui/select/select-item";

/* Prop Types */
interface ReportSceneSkeletonProps {
  title?: string;
  infoModal?: ReactNode;
}

/*
 * ReportSceneSkeleton component
 * Skeleton component for scene/creative reports loading state
 */
export const ReportSceneSkeleton = ({ title = "Visibility", infoModal }: ReportSceneSkeletonProps) => {
  /* Render */
  return (
    <>
      {/* Primary Toolbar */}
      <Toolbar>
        <ToolbarItem>
          <div className="flex items-center gap-2">
            <h1 className="font-semibold">{title}</h1>
            {infoModal && <InfoModalTrigger modalContent={infoModal} />}
          </div>
        </ToolbarItem>

        <ToolbarItem>
          <Button disabled>
            <PencilSquareIcon className="size-3.5" />
            Edit study
          </Button>
          <Button disabled>
            <ArrowUpOnSquareIcon className="size-3.5" />
            Export
          </Button>
        </ToolbarItem>
      </Toolbar>

      {/* Secondary Toolbar with filters and view options */}
      <Toolbar>
        <ToolbarItem>
          {/* Metric Buttons */}
          <Button disabled>Visibility</Button>
          <Button disabled>POP</Button>
          <Button disabled>SOA</Button>
          <Button disabled>POW</Button>
        </ToolbarItem>

        <ToolbarItem>
          <Select defaultValue="desc" onChange={() => {}} disabled>
            <SelectItem value="desc">Sort by: Highest score</SelectItem>
            <SelectItem value="asc">Sort by: Lowest score</SelectItem>
          </Select>
        </ToolbarItem>
      </Toolbar>

      {/* View Options */}
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 ">
          {Array.from({ length: 3 }).map((_, index) => (
            <CustomDropdown
              key={index}
              triggerBtn={
                <Button className="w-full h-14 max-h-14" disabled>
                  <div className="flex flex-1"></div>
                  <ChevronDownIcon className="size-4" />
                </Button>
              }
              triggerBtnClassname="p-0"
            >
              <CustomDropdownItem>All</CustomDropdownItem>
            </CustomDropdown>
          ))}
        </div>
        {/* Card Skeletons Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="card bg-base-100 border-2 border-base-300 min-w-72 w-full rounded-2xl">
              {/* Skeleton Image */}
              <div className="skeleton min-h-[324px] max-h-[324px] w-full rounded-t-2xl rounded-b-none" />

              {/* Skeleton Content */}
              <div className="px-4 py-5 flex flex-col gap-5">
                {/* Title and main metric */}

                <div className="skeleton h-3 w-full rounded-lg" />

                {/* Sub-metrics */}
                <div className="flex flex-col gap-3">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="flex flex-row items-center justify-between gap-2">
                      <div className="skeleton h-4 w-4  rounded-full" />
                      <div className="skeleton h-4 w-full flex-1 rounded-lg" />
                      <div className="skeleton h-4 w-20 rounded-lg" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
